// Analytics controller — PostHog + session recording, strictly consent-gated.
//
// Responsibilities:
//   - Never initialise PostHog before explicit analytics consent.
//   - Never initialise PostHog on Emergent preview hosts, localhost, or when
//     the site is being viewed in an editorial "?preview=" mode.
//   - Configure PostHog with privacy-safe defaults on initialisation:
//       * mask every <input>, <textarea>, and [contenteditable] element in
//         session recordings so email, name, contact-form messages, newsletter
//         addresses, passwords, etc. never leave the browser.
//       * do not create anonymous person profiles unless explicitly identified.
//       * do not record cross-origin iframes.
//   - Provide a clean stop() path that opts out, resets identity, and removes
//     PostHog cookies/localStorage so declined visitors leave no analytics
//     fingerprint behind.
//
// This module is the ONLY place PostHog is initialised. index.html no longer
// contains any analytics code.

// Preview key + host are hard-coded to match what was previously inline in
// index.html. In production, the Emergent build sets REACT_APP_POSTHOG_KEY
// and REACT_APP_POSTHOG_HOST at build time and those win.
const POSTHOG_KEY =
  process.env.REACT_APP_POSTHOG_KEY ||
  "phc_sXJ8eue699HkWdn2xi9KanzrbULqadZjai4ych6zYDS5";
const POSTHOG_HOST =
  process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com";

// Any host that matches any of these is treated as an editorial / preview
// environment and MUST NOT emit analytics traffic even if consent is granted.
const PREVIEW_HOST_PATTERNS = [
  /localhost$/i,
  /^127\./,
  /\.preview\.emergentagent\.com$/i,
  /\.emergent\.host$/i,
  /\.emergentagent\.com$/i,
];

let posthogInstance = null;   // reference to the loaded posthog global
let initState = "idle";       // "idle" | "loading" | "ready" | "declined"

const isBrowser = () => typeof window !== "undefined" && typeof document !== "undefined";

const isPreviewEnvironment = () => {
  if (!isBrowser()) return true;
  const host = window.location.hostname || "";
  if (PREVIEW_HOST_PATTERNS.some((rx) => rx.test(host))) return true;
  // Editorial preview modes (?preview=perspectives, ?preview=1, etc.) should
  // also be excluded from analytics — reviewers should not create traffic.
  try {
    const p = new URL(window.location.href).searchParams.get("preview");
    if (p && p !== "off") return true;
    if (window.sessionStorage && window.sessionStorage.getItem("ttl101_preview_perspectives") === "1") return true;
  } catch { /* noop */ }
  return false;
};

// Inject PostHog's official loader snippet. It creates window.posthog and
// asynchronously loads /static/array.js. We wait until it's replaced with the
// real instance (window.posthog.__loaded === true), then resolve. Timeout
// after 8s so a network hiccup never blocks the app.
const injectPostHog = () =>
  new Promise((resolve, reject) => {
    if (window.posthog && window.posthog.__loaded) return resolve(window.posthog);

    const snippet = document.createElement("script");
    snippet.type = "text/javascript";
    snippet.text = `!function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init me ws ys ps bs capture je Di ks register register_once register_for_session unregister unregister_for_session Ps getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSurveysLoaded onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey canRenderSurveyAsync identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty Es $s createPersonProfile Is opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing Ss debug xs getPageViewId captureTraceFeedback captureTraceMetric".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);`;
    document.head.appendChild(snippet);

    // Poll for readiness of the real instance
    const started = Date.now();
    const tick = () => {
      if (window.posthog && typeof window.posthog.capture === "function" && window.posthog.__loaded) {
        return resolve(window.posthog);
      }
      if (Date.now() - started > 8000) return reject(new Error("PostHog load timeout"));
      setTimeout(tick, 100);
    };
    tick();
  });

// Public: initialise analytics AFTER consent is granted. Safe to call multiple
// times — subsequent calls are no-ops.
export const initAnalytics = async () => {
  if (!isBrowser()) return;
  if (initState === "ready" || initState === "loading") return;
  if (isPreviewEnvironment()) {
    initState = "declined"; // no traffic from preview environments
    return;
  }
  if (!POSTHOG_KEY) return;
  initState = "loading";
  try {
    await injectPostHog();
    // Call posthog.init with privacy-safe options. Session recordings mask
    // every input, textarea and contenteditable — no typed personal
    // information ever leaves the browser.
    window.posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      person_profiles: "identified_only",
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: {
        // Do not autocapture events on password fields (defence in depth).
        css_selector_allowlist: undefined,
        dom_event_allowlist: ["click", "change", "submit"],
      },
      session_recording: {
        maskAllInputs: true,
        maskInputOptions: {
          password: true, email: true, tel: true, number: true, search: true,
          date: true, "datetime-local": true, month: true, time: true, week: true,
          url: true, text: true,
        },
        maskTextSelector: "input, textarea, [contenteditable], [data-mask], [data-testid*='email'], [data-testid*='newsletter'], [data-testid*='contact']",
        recordCrossOriginIframes: false,
        capturePerformance: false,
      },
      disable_session_recording: false,
      opt_out_capturing_by_default: false,
      // Do not persist to localStorage keys before opt-in in edge cases.
      persistence: "localStorage+cookie",
      cross_subdomain_cookie: false,
    });
    posthogInstance = window.posthog;
    initState = "ready";
  } catch (err) {
    initState = "declined";
    // No analytics on failure. Silent — do not disrupt the user's session.
  }
};

// Public: called on decline / forget / withdrawal. Idempotent. Turns capture
// off, resets identity, and removes cookies + localStorage keys PostHog wrote.
export const stopAnalytics = () => {
  if (!isBrowser()) return;
  try {
    if (window.posthog && typeof window.posthog.opt_out_capturing === "function") {
      window.posthog.opt_out_capturing();
      if (typeof window.posthog.stopSessionRecording === "function") {
        window.posthog.stopSessionRecording();
      }
      if (typeof window.posthog.reset === "function") {
        window.posthog.reset(true); // clears distinct_id, session, feature flags
      }
    }
  } catch { /* noop */ }
  // Best-effort cleanup of any cookies or storage keys that PostHog may have
  // written before or after opt-out. This runs unconditionally so a returning
  // visitor who declines never carries analytics state from a previous visit.
  try {
    const host = window.location.hostname;
    document.cookie.split(";").forEach((c) => {
      const name = c.split("=")[0].trim();
      if (name.startsWith("ph_") || name.startsWith("posthog") || name.includes("posthog")) {
        document.cookie = `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT`;
        document.cookie = `${name}=; Path=/; Domain=${host}; Expires=Thu, 01 Jan 1970 00:00:01 GMT`;
        const parts = host.split(".");
        if (parts.length > 1) {
          document.cookie = `${name}=; Path=/; Domain=.${parts.slice(-2).join(".")}; Expires=Thu, 01 Jan 1970 00:00:01 GMT`;
        }
      }
    });
    ["ph_" + POSTHOG_KEY + "_posthog", "phc_analytics", "posthog", "__ph_opt_in_out_" + POSTHOG_KEY]
      .concat(Object.keys(window.localStorage).filter((k) => k.startsWith("ph_") || k.includes("posthog")))
      .forEach((k) => {
        try { window.localStorage.removeItem(k); } catch { /* noop */ }
        try { window.sessionStorage.removeItem(k); } catch { /* noop */ }
      });
  } catch { /* noop */ }
  initState = "declined";
  posthogInstance = null;
};

// Public helper — useful for tests and reporting.
export const analyticsStatus = () => ({
  state: initState,
  preview: isPreviewEnvironment(),
  loaded: !!(isBrowser() && window.posthog && window.posthog.__loaded),
});
